(function () {
  'use strict';

  /* ============================================================
   * KOBO Gift Sync — Optimized Version (patched)
   * Pure event-driven free gift auto-synchronization logic.
   *
   * Fixes vs previous version:
   * - Removed 'theme:loading:end' listener: this event fires on
   *   many unrelated page/section loads (quick view, filters,
   *   AJAX navigation), causing far more background /cart.js and
   *   /cart/add.js|update.js calls than necessary. Those extra
   *   calls were competing with the actual Add-to-Cart click for
   *   the browser's limited concurrent connections to the same
   *   domain, which is the most likely cause of the "slow / flickers
   *   then stops" button behavior reported.
   * - Added a short cooldown after any cart-mutating event so a
   *   rapid burst of events (e.g. clicking +/- repeatedly) doesn't
   *   fire overlapping syncs that can race with the customer's own
   *   quantity changes.
   * - The old script dispatched a 'cart:refresh' custom event after
   *   syncing, assuming the theme would re-render on it. The theme
   *   does not listen for that event, so after the gift item's
   *   quantity/price was corrected in the backend, the /cart page UI
   *   stayed stale (old quantity/price shown) until a manual reload.
   *   Fix: on the /cart page specifically, do a guarded one-time
   *   reload after a real change so the totals shown are always
   *   accurate. Elsewhere (product pages, drawer) we still just
   *   dispatch the event, since a full reload there would be
   *   disruptive and isn't needed for those flows.
   * ============================================================ */

  var THUNDER_AXE_VARIANTS = ['43239706263617'];
  var THUNDER_AXE_GIFT_ID  = '44225127022657';

  var TSUNAMI_VARIANTS     = ['41865540632641'];
  var TSUNAMI_GIFT_ID      = '44255654346817';

  var HAT_VARIANTS         = ['41865837903937', '41865837871169', '41865837936705', '41865837969473'];
  var HAT_ADD_VARIANT      = '41865837936705';

  var DEBOUNCE_MS = 350;
  var COOLDOWN_MS = 600;
  var RELOAD_GUARD_KEY = 'kobo_gift_sync_last_reload';
  var RELOAD_GUARD_WINDOW_MS = 1200;
  var nativeFetch = window.fetch.bind(window);

  var isSyncing = false;
  var debounceTimer = null;
  var abortCtrl = null;
  var lastRunAt = 0;

  function safeGetSession(key) {
    try { return window.sessionStorage ? sessionStorage.getItem(key) : null; }
    catch (e) { return null; }
  }
  function safeSetSession(key, value) {
    try { if (window.sessionStorage) sessionStorage.setItem(key, value); }
    catch (e) { /* private mode etc. - fail silently */ }
  }
  function isCartPage() {
    try { return window.location.pathname === '/cart'; }
    catch (e) { return false; }
  }

  // Lightweight refresh: fetch the /cart page HTML in the background and
  // swap only the #MainContent container, instead of a full browser
  // reload. This avoids re-downloading CSS/JS/images, which is what made
  // the previous full window.location.reload() feel slow on mobile/Android.
  // Falls back to a full reload only if this soft refresh fails.
  function softRefreshCartPage() {
    nativeFetch('/cart?t=' + Date.now(), { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart page fetch failed');
        return r.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var newDoc = parser.parseFromString(html, 'text/html');
        var newMain = newDoc.getElementById('MainContent');
        var currentMain = document.getElementById('MainContent');

        if (newMain && currentMain) {
          currentMain.innerHTML = newMain.innerHTML;
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } else {
          throw new Error('MainContent container not found');
        }
      })
      .catch(function (err) {
        console.error('[KOBO GiftSync] Soft refresh failed, falling back to full reload:', err);
        try { window.location.reload(); } catch (e) {}
      });
  }

  /* ---------- Promo Eligibility Check ---------- */
  try {
    var qs = window.location.search;
    if (qs.indexOf('promo=kobofreehat') !== -1 || qs.indexOf('KOBOHAT') !== -1) {
      sessionStorage.setItem('kobo_hat_eligible', 'true');
    }
  } catch (e) {}

  function isHatEligible() {
    try { return sessionStorage.getItem('kobo_hat_eligible') === 'true'; }
    catch (e) { return false; }
  }

  function isCheckoutContext() {
    var p = window.location.pathname.toLowerCase();
    return p.indexOf('/checkout') !== -1 || p.indexOf('/wallets') !== -1 || p.indexOf('/pay') !== -1;
  }

  /* ---------- Helpers ---------- */
  function qty(items, ids) {
    return (items || [])
      .filter(function (i) { return i && ids.indexOf(String(i.variant_id)) !== -1; })
      .reduce(function (sum, i) { return sum + (Number(i.quantity) || 0); }, 0);
  }

  function findItem(items, ids) {
    return (items || []).find(function (i) { return i && ids.indexOf(String(i.variant_id)) !== -1; });
  }

  /* ---------- Core Sync Execution ---------- */
  function triggerSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      runSync();
    }, DEBOUNCE_MS);
  }

  function runSync() {
    if (isCheckoutContext() || isSyncing) return;

    var now = Date.now();
    if (now - lastRunAt < COOLDOWN_MS) return;

    isSyncing = true;
    lastRunAt = now;

    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();
    var signal = abortCtrl.signal;

    nativeFetch('/cart.js?t=' + Date.now(), { signal: signal, cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed');
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) {
          isSyncing = false;
          return;
        }

        var thunderQty = qty(cart.items, THUNDER_AXE_VARIANTS);
        var tsunamiQty = qty(cart.items, TSUNAMI_VARIANTS);

        var thunderGift = findItem(cart.items, [THUNDER_AXE_GIFT_ID]);
        var tsunamiGift = findItem(cart.items, [TSUNAMI_GIFT_ID]);
        var hatItem     = findItem(cart.items, HAT_VARIANTS);

        var targetHatQty = isHatEligible() ? thunderQty : 0;

        var add = [];
        var update = {};

        // Calculate Thunder Axe Gift
        if (!thunderGift && thunderQty > 0) add.push({ id: THUNDER_AXE_GIFT_ID, quantity: thunderQty });
        else if (thunderGift && thunderGift.quantity !== thunderQty) update[THUNDER_AXE_GIFT_ID] = thunderQty;

        // Calculate Tsunami Gift
        if (!tsunamiGift && tsunamiQty > 0) add.push({ id: TSUNAMI_GIFT_ID, quantity: tsunamiQty });
        else if (tsunamiGift && tsunamiGift.quantity !== tsunamiQty) update[TSUNAMI_GIFT_ID] = tsunamiQty;

        // Calculate Free Hat
        if (!hatItem && targetHatQty > 0) add.push({ id: HAT_ADD_VARIANT, quantity: targetHatQty });
        else if (hatItem && hatItem.quantity !== targetHatQty) update[hatItem.variant_id] = targetHatQty;

        if (add.length === 0 && Object.keys(update).length === 0) {
          isSyncing = false;
          return;
        }

        var jobs = [];
        if (add.length > 0) {
          jobs.push(nativeFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: add }),
            signal: signal
          }));
        }
        if (Object.keys(update).length > 0) {
          jobs.push(nativeFetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: update }),
            signal: signal
          }));
        }

        return Promise.all(jobs).then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          if (isCartPage()) {
            var lastReload = Number(safeGetSession(RELOAD_GUARD_KEY)) || 0;
            var reloadNow = Date.now();
            if (reloadNow - lastReload > RELOAD_GUARD_WINDOW_MS) {
              safeSetSession(RELOAD_GUARD_KEY, String(reloadNow));
              softRefreshCartPage();
            } else {
              console.warn('[KOBO GiftSync] Refresh skipped - loop guard active');
            }
          }
        });
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') {
          console.error('[KOBO GiftSync Error]:', err);
        }
      })
      .finally(function () {
        isSyncing = false;
      });
  }

  /* ---------- Safe Event Listeners ---------- */

  // Only listen to actual cart-mutation events. Removed 'theme:loading:end'
  // because it fires on unrelated page/section loads and was causing
  // excess background network activity that competed with the
  // Add-to-Cart click itself.
  document.addEventListener('cart:updated', triggerSync);
  document.addEventListener('cart:change', triggerSync);

  // Initial execution when page loads (covers direct /cart page visits)
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    triggerSync();
  } else {
    document.addEventListener('DOMContentLoaded', triggerSync);
  }

})();